import { useState } from 'react';
import {
  RULE_DEFAULT_FEET,
  RULE_KINDS,
  RULE_KIND_HINTS,
  RULE_KIND_LABELS,
  isSeat,
  itemLabel,
  ruleSummary,
  studentName,
  type RoomItem,
  type RuleKind,
  type RuleMode,
  type RuleViolation,
  type SeatingRule,
  type Student,
} from '@td/shared';
import { useCreateRule, useDeleteRule, useUpdateRule } from '../hooks/useSeatingRules';
import { useConfirm } from './ConfirmDialog';
import { ApiRequestError } from '../lib/api';

/**
 * The rules list, and the small form for adding one.
 *
 * Rules are advisory: a broken rule is shown, never enforced. The teacher may
 * have a reason the app can't know about, and a chart that refused to let a
 * child sit somewhere would be worse than useless on the day it is wrong.
 */
export function SeatingRules({
  rules,
  students,
  items,
  violations,
  onHighlight,
}: {
  rules: SeatingRule[];
  students: Student[];
  items: RoomItem[];
  violations: RuleViolation[];
  onHighlight: (itemIds: string[] | null) => void;
}) {
  const create = useCreateRule();
  const update = useUpdateRule();
  const remove = useDeleteRule();
  const confirm = useConfirm();

  const [adding, setAdding] = useState(false);
  const [kind, setKind] = useState<RuleKind>('apart');
  const [studentId, setStudentId] = useState('');
  const [otherStudentId, setOtherStudentId] = useState('');
  const [itemId, setItemId] = useState('');
  const [mode, setMode] = useState<RuleMode>('adjacent');
  const [feet, setFeet] = useState(RULE_DEFAULT_FEET.apart);
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);

  const pair = kind === 'apart' || kind === 'together';
  const nameOf = (id: string) => {
    const s = students.find((x) => x.id === id);
    return s ? `${studentName(s)} ${s.lastName[0] ?? ''}.`.trim() : 'a student who has left';
  };
  const brokenBy = (ruleId: string) => violations.find((v) => v.ruleId === ruleId);

  // Anything in the room can anchor a rule — the board, the door, the reading
  // corner. Seats are excluded: "sit near desk 12" is really a pair rule.
  const anchors = items.filter((i) => !isSeat(i));

  function reset() {
    setKind('apart');
    setStudentId('');
    setOtherStudentId('');
    setItemId('');
    setMode('adjacent');
    setFeet(RULE_DEFAULT_FEET.apart);
    setNote('');
    setError(null);
  }

  async function submit() {
    setError(null);
    try {
      await create.mutateAsync({
        kind,
        mode,
        studentId,
        otherStudentId: pair ? otherStudentId : null,
        itemId: pair ? null : itemId,
        feet,
        note,
      });
      reset();
      setAdding(false);
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : 'That rule could not be saved.');
    }
  }

  const canSubmit = studentId && (pair ? otherStudentId && otherStudentId !== studentId : itemId);

  return (
    <div>
      {rules.length === 0 && !adding && (
        <p className="mb-2 text-[11px] leading-snug text-stone">
          Nothing yet. Rules flag a seating chart that puts the wrong two children together, or
          seats someone too far from the board.
        </p>
      )}

      {rules.length > 0 && (
        <ul className="mb-2 space-y-1.5">
          {rules.map((rule) => {
            const broken = brokenBy(rule.id);
            return (
              <li
                key={rule.id}
                onMouseEnter={() => onHighlight(broken?.itemIds ?? null)}
                onMouseLeave={() => onHighlight(null)}
                className={[
                  'group rounded-lg border px-2 py-1.5 text-[11px] transition-colors',
                  broken ? 'border-rust/60 bg-rose/10' : 'border-sand',
                  rule.active ? '' : 'opacity-50',
                ].join(' ')}
              >
                <div className="flex items-start gap-1.5">
                  <span
                    aria-hidden
                    title={broken ? 'This rule is being broken' : 'Respected'}
                    className={broken ? 'text-rust' : 'text-sage'}
                  >
                    {broken ? '!' : '•'}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="leading-snug text-espresso">{ruleSummary(rule, nameOf, items)}</p>
                    {rule.note && <p className="text-stone">{rule.note}</p>}
                    {broken && <p className="mt-0.5 text-rust">{broken.message}</p>}
                  </div>
                </div>

                <div className="mt-1 flex gap-2 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
                  <button
                    onClick={() => update.mutate({ id: rule.id, active: !rule.active })}
                    className="btn-soft px-1.5 py-0.5 text-[11px]"
                  >
                    {rule.active ? 'Mute' : 'Unmute'}
                  </button>
                  <button
                    onClick={async () => {
                      const ok = await confirm({
                        title: 'Delete this rule?',
                        body: ruleSummary(rule, nameOf, items),
                        confirmLabel: 'Delete',
                        danger: true,
                      });
                      if (ok) remove.mutate(rule.id);
                    }}
                    className="btn-soft-danger px-1.5 py-0.5 text-[11px]"
                  >
                    Delete
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {adding ? (
        <div className="rounded-lg border border-clay bg-linen/40 p-2">
          <div className="mb-2 flex flex-wrap gap-1">
            {RULE_KINDS.map((k) => (
              <button
                key={k}
                onClick={() => {
                  setKind(k);
                  setFeet(RULE_DEFAULT_FEET[k]);
                }}
                aria-pressed={kind === k}
                className={[
                  'rounded-lg border px-1.5 py-0.5 text-[11px] transition-colors',
                  kind === k
                    ? 'border-terracotta bg-terracotta text-cream'
                    : 'border-sand text-walnut hover:bg-linen',
                ].join(' ')}
              >
                {RULE_KIND_LABELS[k]}
              </button>
            ))}
          </div>

          <p className="mb-2 text-[11px] leading-snug text-stone">{RULE_KIND_HINTS[kind]}</p>

          <select
            className="field mb-1.5 py-1 text-xs"
            value={studentId}
            onChange={(e) => setStudentId(e.target.value)}
            aria-label="Student"
          >
            <option value="">Which student…</option>
            {students.map((s) => (
              <option key={s.id} value={s.id}>
                {studentName(s)} {s.lastName}
              </option>
            ))}
          </select>

          {pair ? (
            <select
              className="field mb-1.5 py-1 text-xs"
              value={otherStudentId}
              onChange={(e) => setOtherStudentId(e.target.value)}
              aria-label="And which student"
            >
              <option value="">And…</option>
              {students
                .filter((s) => s.id !== studentId)
                .map((s) => (
                  <option key={s.id} value={s.id}>
                    {studentName(s)} {s.lastName}
                  </option>
                ))}
            </select>
          ) : (
            <select
              className="field mb-1.5 py-1 text-xs"
              value={itemId}
              onChange={(e) => setItemId(e.target.value)}
              aria-label="Which part of the room"
            >
              <option value="">Which part of the room…</option>
              {anchors.map((i) => (
                <option key={i.id} value={i.id}>
                  {itemLabel(i)}
                </option>
              ))}
            </select>
          )}

          {/*
            Adjacency first, because "not next to each other" is what a teacher
            says out loud. Feet are there for the cases that genuinely need a
            distance, like staying within reach of the board.
          */}
          <div className="mb-1.5 flex flex-wrap items-center gap-1.5 text-[11px] text-walnut">
            <button
              onClick={() => setMode('adjacent')}
              aria-pressed={mode === 'adjacent'}
              className={[
                'rounded-lg border px-1.5 py-0.5 transition-colors',
                mode === 'adjacent'
                  ? 'border-terracotta bg-terracotta text-cream'
                  : 'border-sand hover:bg-linen',
              ].join(' ')}
            >
              {kind === 'apart'
                ? 'Never side by side'
                : kind === 'together'
                  ? 'Side by side'
                  : kind === 'nearItem'
                    ? 'Right beside it'
                    : 'Never beside it'}
            </button>
            <button
              onClick={() => setMode('feet')}
              aria-pressed={mode === 'feet'}
              className={[
                'rounded-lg border px-1.5 py-0.5 transition-colors',
                mode === 'feet'
                  ? 'border-terracotta bg-terracotta text-cream'
                  : 'border-sand hover:bg-linen',
              ].join(' ')}
            >
              By distance
            </button>

            {mode === 'feet' && (
              <span className="flex items-center gap-1">
                {kind === 'apart' || kind === 'awayFromItem' ? 'at least' : 'within'}
                <input
                  className="field w-14 py-1 text-xs"
                  type="number"
                  min={1}
                  max={100}
                  value={feet}
                  onChange={(e) => setFeet(Math.max(1, Number(e.target.value) || 1))}
                  aria-label="Distance in feet"
                />
                ft
              </span>
            )}
          </div>

          <input
            className="field mb-2 py-1 text-xs"
            placeholder="Why, in a few words"
            value={note}
            maxLength={200}
            onChange={(e) => setNote(e.target.value)}
            aria-label="Note"
          />

          {error && <p className="mb-2 text-[11px] text-rust">{error}</p>}

          <div className="flex justify-end gap-2">
            <button
              onClick={() => {
                reset();
                setAdding(false);
              }}
              className="btn-secondary px-2 py-1 text-[11px]"
            >
              Cancel
            </button>
            <button
              onClick={() => void submit()}
              disabled={!canSubmit || create.isPending}
              className="btn-primary px-2 py-1 text-[11px]"
            >
              {create.isPending ? 'Saving…' : 'Add rule'}
            </button>
          </div>
        </div>
      ) : (
        <button
          onClick={() => setAdding(true)}
          disabled={students.length === 0}
          className="btn-soft-accent w-full"
        >
          + Add a rule
        </button>
      )}
    </div>
  );
}
